import { useState } from "react";
import { motion } from "framer-motion";
import { Moon, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n/I18nContext";
import { formatApiErrorDetail } from "@/lib/api";
import LanguageSwitcher from "@/components/LanguageSwitcher";

export default function Auth() {
  const { login, register } = useAuth();
  const { t } = useI18n();
  const [mode, setMode] = useState("register");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(name, email, password);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex flex-col bg-background overflow-hidden">
      <img
        src="https://images.unsplash.com/photo-1546146477-15a587cd3fcb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGRhcmslMjBtaWRuaWdodCUyMHRlY2glMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc5MDQ5OTM5MXww&ixlib=rb-4.1.0&q=85"
        alt=""
        className="absolute inset-0 h-full w-full object-cover opacity-25"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-background/60 via-background/80 to-background" />

      <header className="relative z-10 flex items-center justify-between px-5 pt-6">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-lg bg-primary flex items-center justify-center">
            <Moon className="h-5 w-5 text-primary-foreground" />
          </div>
          <span className="font-display text-xl font-extrabold">{t("brand")}</span>
        </div>
        <LanguageSwitcher />
      </header>

      <div className="relative z-10 flex-1 flex flex-col justify-center px-5 pb-10 max-w-md w-full mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold leading-tight">
            {t("tagline")}
          </h1>
          <p className="mt-3 text-muted-foreground">
            {mode === "login" ? t("authSubLogin") : t("authSubRegister")}
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            {mode === "register" && (
              <div className="space-y-1.5">
                <Label htmlFor="name">{t("name")}</Label>
                <Input id="name" data-testid="auth-name-input" value={name}
                  onChange={(e) => setName(e.target.value)} required
                  className="bg-secondary/70 border-white/10 h-12" />
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="email">{t("email")}</Label>
              <Input id="email" type="email" data-testid="auth-email-input" value={email}
                onChange={(e) => setEmail(e.target.value)} required
                className="bg-secondary/70 border-white/10 h-12" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">{t("password")}</Label>
              <Input id="password" type="password" data-testid="auth-password-input" value={password}
                onChange={(e) => setPassword(e.target.value)} required
                className="bg-secondary/70 border-white/10 h-12" />
            </div>

            {error && (
              <p data-testid="auth-error" className="text-sm text-destructive">{error}</p>
            )}

            <Button type="submit" data-testid="auth-submit-btn" disabled={loading}
              className="w-full h-12 text-base font-semibold group">
              {loading ? t("processing") : mode === "login" ? t("loginCta") : t("registerCta")}
              {!loading && <ArrowRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-1" />}
            </Button>
          </form>

          <button
            data-testid="auth-toggle-mode"
            onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
            className="mt-6 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            {mode === "login" ? t("noAccount") + " " : t("haveAccount") + " "}
            <span className="text-primary font-semibold">
              {mode === "login" ? t("signUp") : t("signIn")}
            </span>
          </button>
        </motion.div>
      </div>
    </div>
  );
}
